import { queryClient } from "@/query-client";
import { queryOptions, useMutation } from "@tanstack/react-query";
import type { JournalAccountPurpose } from "../../types/chart-of-accounts";
import journalAccountsService from "../services/journal-accounts-service";

export const chartOfAccountsQueryOptions = () =>
	queryOptions({
		queryKey: ["books", "chart-of-accounts"],
		queryFn: () => journalAccountsService.listChartOfAccounts(),
		staleTime: 5 * 60_000,
	});

export const journalAccountSettingsQueryOptions = () =>
	queryOptions({
		queryKey: ["journal-account-settings"],
		queryFn: () => journalAccountsService.getSettings(),
		staleTime: 60_000,
	});

export const useUpdateJournalAccountSettingsMutation = () =>
	useMutation({
		mutationFn: (settings: Array<{ purpose: JournalAccountPurpose; account_code: string }>) => journalAccountsService.updateSettings(settings),
		meta: { successMessage: "Default journal accounts saved" },
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["journal-account-settings"] });
			queryClient.invalidateQueries({ queryKey: ["invoice"] });
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useUpdateInvoiceJournalOverridesMutation = (invoiceId: string) =>
	useMutation({
		mutationFn: (overrides: Partial<Record<JournalAccountPurpose, string | null>>) => journalAccountsService.updateInvoiceOverrides(invoiceId, overrides),
		meta: { successMessage: "Account override saved" },
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice", invoiceId] });
			// The Collection Receipt tab on the request page also resolves
			// accounts via the linked invoice's overrides, so its query has
			// to be refetched too.
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useUpdateInvoiceRequestJournalOverridesMutation = (requestId: string) =>
	useMutation({
		mutationFn: (overrides: Partial<Record<JournalAccountPurpose, string | null>>) =>
			journalAccountsService.updateInvoiceRequestOverrides(requestId, overrides),
		meta: { successMessage: "Account override saved" },
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-request", requestId] });
		},
	});

export const collectionItemsQueryOptions = (requestId: string) =>
	queryOptions({
		queryKey: ["invoice-request", requestId, "collection-items"],
		queryFn: () => journalAccountsService.getCollectionItems(requestId),
		staleTime: 30_000,
	});

export const usePostCollectionReceiptMutation = (requestId: string) =>
	useMutation({
		mutationKey: ["invoice-request", requestId, "collection-receipts", "post"],
		mutationFn: (status: "draft" | "posted" = "posted") => journalAccountsService.postCollectionReceipt(requestId, status),
		meta: { successMessage: "Collection Receipt sent to Books" },
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-request", requestId] });
		},
	});
